import { MdAdd } from "react-icons/md"
import { Button } from "./ResultsComponents"
import AddCAModal from "./AddCAModal";
import { useState, useEffect } from "react";

export default function ContinousAssessment({ ca, setCa, termTerminology, setTermTerminology, error, setError }) {
  // Local state for modal and validation
  const [modalOpen, setModalOpen] = useState(false);
  const [validationError, setValidationError] = useState("");

  // Get current values from props
  const caMaxScore = ca?.max_score || 40;
  const caList = Array.isArray(ca?.cas) ? ca.cas : [];

  // Handler for the total CA max_score (robust controlled input)
  const handleTotalWeightChange = (e) => {
    const value = e.target.value;
    // Allow empty string for controlled input
    if (value === "") {
      setCa({ ...ca, max_score: "" });
      setValidationError("");
      setError(false);
      return;
    }
    const parsed = parseInt(value, 10);
    if (isNaN(parsed) || parsed < 0 || parsed > 100) {
      setValidationError("CA Score must be between 0 and 100");
      setError(true);
      setCa({ ...ca, max_score: value });
      return;
    }
    // Check if current CA list total exceeds new max score
    const currentTotal = caList.reduce((sum, caItem) => sum + (caItem.max_score || 0), 0);
    if (currentTotal > parsed) {
      setValidationError(`Current CA total (${currentTotal}) exceeds new max score (${parsed})`);
      setError(true);
      setCa({ ...ca, max_score: value });
      return;
    }
    setValidationError("");
    setError(false);
    setCa({ ...ca, max_score: parsed, cas: caList });
  };

  // Handler for adding a CA
  const handleAddCA = (caObj) => {
    if (caList.length >= 3) {
      setValidationError("Maximum of 3 assessments allowed");
      return;
    }

    // Check if adding this CA would exceed max score
    const currentTotal = caList.reduce((sum, caItem) => sum + (caItem.max_score || 0), 0);
    const newTotal = currentTotal + caObj.max_score;
    
    if (newTotal > caMaxScore) {
      setValidationError(`Adding this CA (${caObj.max_score}) would exceed total CA score (${caMaxScore})`);
      return;
    }

    const newList = [...caList, caObj];
    setValidationError("");
    setError(false);
    setCa({ ...ca, cas: newList, max_score: caMaxScore });
    setModalOpen(false);
  };

  // Handler for removing a CA
  const handleRemoveCA = (idx) => {
    const newList = caList.filter((_, i) => i !== idx);
    setValidationError("");
    setError(false);
    setCa({ ...ca, cas: newList, max_score: caMaxScore });
  };

  // Handler for term terminology
  const handleTermTerminologyChange = (e) => {
    const value = e.target.value;
    setTermTerminology(value);
    // Only validate on blur or submit, not on every keystroke
    setValidationError("");
    setError(false);
  };

  // Optionally, add validation on blur
  const handleTermTerminologyBlur = (e) => {
    const value = e.target.value;
    if (value.trim().length === 0) {
      setValidationError("Term terminology cannot be empty");
      setError(true);
    } else {
      setValidationError("");
      setError(false);
    }
  };

  // Validation: sum of CA max_scores should not exceed caMaxScore
  const totalCAScore = caList.reduce((sum, caItem) => sum + (caItem.max_score || 0), 0);
  const showScoreError = totalCAScore > caMaxScore;

  // Update parent error state when validation changes
  useEffect(() => {
    const hasError = showScoreError || validationError.length > 0 || !termTerminology.trim();
    setError(hasError);
  }, [showScoreError, validationError, termTerminology, setError]);

  return (
    <div>
      {/* Term Terminology input */}
      <div className="my-3">
        <label className="text-sm font-semibold">
          Term Terminology <span className="text-red-500">*</span>
          <span className="text-xs text-gray-500 ml-2">(e.g. Term, Semester, Cycle)</span>
        </label>
        <input
          value={termTerminology}
          onChange={handleTermTerminologyChange}
          onBlur={handleTermTerminologyBlur}
          className="block my-1 w-full rounded-md text-black font-semibold border border-gray-300 p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          maxLength={20}
          placeholder="Term"
        />
        {!termTerminology.trim() && (
          <p className="text-red-500 text-sm mt-1">Term terminology is required</p>
        )}
      </div>

      {/* Total Weight input */}
      <div className="bg-[#EEEEFC] my-3 p-4 rounded-3xl flex justify-between items-start gap-3 border">
        <div className="flex-1">
          <p className="text-base font-semibold">Total CA Weight</p>
          <p className="text-xs text-gray-500 mt-1">
            Total score allocated to Continuous Assessment (Exam will be {100 - caMaxScore})
          </p>
        </div>
        <div className="flex-1 max-w-[200px]">
          <label className="text-sm font-semibold">Total CA Score <span className="text-red-500">*</span></label>
          <input 
            value={ca.max_score === undefined || ca.max_score === null ? "" : ca.max_score} 
            onChange={handleTotalWeightChange} 
            type="number" 
            min={0} 
            max={100}
            className="block my-1 w-full rounded-md text-black font-semibold border border-gray-300 p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
          />
          <p className="text-xs text-gray-500 mt-1">Range: 0-100</p>
        </div>
      </div>

      {/* Current CA Usage Display */}
      {caList.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-blue-800">
              CA Score Used: {totalCAScore} / {caMaxScore}
            </span>
            <span className="text-sm text-blue-600">
              Remaining: {caMaxScore - totalCAScore}
            </span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2 mt-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                showScoreError ? 'bg-red-500' : 'bg-blue-500'
              }`}
              style={{ width: `${Math.min((totalCAScore / caMaxScore) * 100, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Render each CA */}
      {caList.map((currentCa, idx) => (
        <div key={`${currentCa.title}-${idx}`} className="my-3 p-4 rounded-3xl flex justify-between items-start gap-3 border bg-white hover:bg-gray-50 transition-colors">
          <div className="flex-1">
            <p className="text-base font-semibold text-gray-800">{currentCa.title}</p>
            {currentCa.description && (
              <p className="text-xs text-gray-500 mt-1">{currentCa.description}</p>
            )}
          </div>
          <div className="flex-1 max-w-[150px]">
            <label className="text-sm text-gray-600">Maximum Score</label>
            <div className="flex items-center gap-2">
              <input 
                value={currentCa.max_score} 
                type="number" 
                min={0} 
                disabled 
                className="block my-1 w-full rounded-md text-black font-semibold bg-gray-100 border border-gray-300 p-2" 
              />
              <span className="text-sm text-gray-500">pts</span>
            </div>
          </div>
          <Button 
            variant={2} 
            onClick={() => handleRemoveCA(idx)} 
            className="ml-2 hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            Remove
          </Button>
        </div>
      ))}

      {/* Add CA Button */}
      <Button 
        onClick={() => setModalOpen(true)} 
        variant={2} 
        full 
        disabled={caList.length >= 3}
        className="mt-4"
      >
        <MdAdd className="mr-1" /> 
        Add Continuous Assessment
        {caList.length >= 3 && " (Maximum Reached)"}
      </Button>

      {/* Error Messages */}
      {caList.length >= 3 && (
        <p className="text-amber-600 text-sm mt-2 flex items-center">
          <span className="inline-block w-4 h-4 bg-amber-100 rounded-full mr-2 flex items-center justify-center">
            <span className="text-xs">!</span>
          </span>
          Maximum of 3 continuous assessments allowed.
        </p>
      )}
      
      {showScoreError && (
        <p className="text-red-500 text-sm mt-2 flex items-center">
          <span className="inline-block w-4 h-4 bg-red-100 rounded-full mr-2 flex items-center justify-center">
            <span className="text-xs">×</span>
          </span>
          Sum of CA scores ({totalCAScore}) exceeds total CA weight ({caMaxScore})
        </p>
      )}

      {validationError && (
        <p className="text-red-500 text-sm mt-2 flex items-center">
          <span className="inline-block w-4 h-4 bg-red-100 rounded-full mr-2 flex items-center justify-center">
            <span className="text-xs">×</span>
          </span>
          {validationError}
        </p>
      )}

      {/* Add CA Modal */}
      <AddCAModal 
        open={modalOpen} 
        onClose={() => {
          setModalOpen(false);
          setValidationError("");
        }} 
        onSave={handleAddCA} 
        existingTitles={caList.map(ca => ca.title)}
        maxScoreRemaining={caMaxScore - totalCAScore}
      />
    </div>
  );
}